import React from 'react';
import { Box, Typography, Stack, Divider } from '@mui/material';
import { AppointmentTypeChanges } from '../../types/appointmentChanges';
import { AppointmentType, ExpandableFieldItem } from '@features/appointment-types';
import { formatAppointmentDuration } from '../../../../utils/durationUtils';
import { UserTemplateItem } from '../../../../types/types';

interface UpdatePreviewContentProps {
  appointmentTypeName: string;
  changes?: AppointmentTypeChanges;
  selectedAppointmentType?: AppointmentType;
  currentDuration?: number;
  currentTelehealth?: boolean;
  currentForms?: ExpandableFieldItem[];
  currentReminders?: ExpandableFieldItem[];
  currentTemplate?: UserTemplateItem | null;
}

export const UpdatePreviewContent: React.FC<UpdatePreviewContentProps> = ({
  appointmentTypeName,
  changes,
  selectedAppointmentType,
  currentDuration,
  currentTelehealth,
  currentForms = [],
  currentReminders = [],
  currentTemplate,
}) => {
  const hasFormChanges =
    changes?.forms &&
    (changes.forms.added.length > 0 ||
      changes.forms.removed.length > 0 ||
      changes.forms.modified.length > 0);

  const hasReminderChanges =
    changes?.reminders &&
    (changes.reminders.added.length > 0 ||
      changes.reminders.removed.length > 0 ||
      changes.reminders.modified.length > 0);

  // Get all current forms that aren't being removed, modified, or newly added
  const removedFormIds = new Set(changes?.forms?.removed.map(f => f.formId) || []);
  const addedFormIds = new Set(changes?.forms?.added.map(f => f.formId).filter(Boolean) || []);
  const modifiedFormIds = new Set(
    changes?.forms?.modified.map(f => f.formId).filter(Boolean) || [],
  );

  const unchangedForms = currentForms.filter(
    f =>
      !removedFormIds.has(f.formId) &&
      !addedFormIds.has(f.formId) &&
      !modifiedFormIds.has(f.formId),
  );

  // Get all current reminders that aren't being removed, modified, or newly added
  const addedReminderCount = changes?.reminders?.added.length || 0;
  const removedReminderCount = changes?.reminders?.removed.length || 0;
  const modifiedReminderCount = changes?.reminders?.modified.length || 0;

  // Calculate how many reminders should be considered "unchanged"
  const unchangedReminderCount = Math.max(0, currentReminders.length - addedReminderCount);

  // Get only the unchanged reminders (exclude the newly added ones at the end)
  const unchangedReminders = currentReminders
    .slice(0, unchangedReminderCount)
    .filter((reminder, idx) => {
      // Also exclude any that are marked as modified
      const isModified = idx < modifiedReminderCount;
      return !isModified;
    });

  return (
    <Stack spacing={1.5}>
      {/* Template */}
      {currentTemplate && (
        <>
          <Box>
            <Typography variant="caption" color="text.secondary" sx={{ mb: 0.5, display: 'block' }}>
              Template
            </Typography>
            <Typography variant="body2">
              {currentTemplate.display_name || 'Unnamed Template'}
            </Typography>
          </Box>
          <Divider />
        </>
      )}

      {/* Duration */}
      <Box>
        <Typography variant="caption" color="text.secondary" sx={{ mb: 0.5, display: 'block' }}>
          Duration
        </Typography>
        {changes?.duration ? (
          <Stack direction="row" spacing={1} sx={{ alignItems: "center" }}>
            <Typography
              variant="body2"
              sx={{
                textDecoration: 'line-through',
                color: 'text.disabled',
              }}
            >
              {changes.duration.old} min
            </Typography>
            <Typography variant="body2">→</Typography>
            <Typography variant="body2" sx={{ fontWeight: 500 }}>
              {changes.duration.new} min
            </Typography>
          </Stack>
        ) : (
          <Typography variant="body2">
            {currentDuration || selectedAppointmentType?.default_duration || 30} min
          </Typography>
        )}
      </Box>

      {/* Telehealth */}
      {(changes?.telehealth || currentTelehealth !== undefined) && (
        <>
          <Divider />
          <Box>
            <Typography variant="caption" color="text.secondary" sx={{ mb: 0.5, display: 'block' }}>
              Type
            </Typography>
            {changes?.telehealth ? (
              <Stack direction="row" spacing={1} sx={{ alignItems: "center" }}>
                <Typography
                  variant="body2"
                  sx={{
                    textDecoration: 'line-through',
                    color: 'text.disabled',
                  }}
                >
                  {changes.telehealth.old ? 'Telehealth' : 'In-person'}
                </Typography>
                <Typography variant="body2">→</Typography>
                <Typography variant="body2" sx={{ fontWeight: 500 }}>
                  {changes.telehealth.new ? 'Telehealth' : 'In-person'}
                </Typography>
              </Stack>
            ) : (
              <Typography variant="body2">
                {currentTelehealth ? 'Telehealth' : 'In-person'}
              </Typography>
            )}
          </Box>
        </>
      )}

      {/* Reminders */}
      {(hasReminderChanges || currentReminders.length > 0) && (
        <>
          <Divider />
          <Box>
            <Typography variant="caption" color="text.secondary" sx={{ mb: 0.5, display: 'block' }}>
              Reminders
            </Typography>
            <Stack spacing={0.5}>
              {/* Show unchanged reminders first */}
              {unchangedReminders.map((reminder, idx) => (
                <Typography key={`unchanged-${idx}`} variant="caption">
                  Appointment Reminder · {formatAppointmentDuration(reminder.offset || '')}
                </Typography>
              ))}

              {/* Then show changes */}
              {changes?.reminders?.added.map((reminder, idx) => (
                <Typography key={`add-${idx}`} variant="caption" sx={{ fontWeight: 700 }}>
                  Appointment Reminder · {reminder.timing}
                </Typography>
              ))}
              {changes?.reminders?.removed.map((reminder, idx) => (
                <Typography
                  key={`rem-${idx}`}
                  variant="caption"
                  sx={{
                    textDecoration: 'line-through',
                    color: 'text.disabled',
                  }}
                >
                  Appointment Reminder · {reminder.timing}
                </Typography>
              ))}
              {changes?.reminders?.modified.map((reminder, idx) => (
                <Stack key={`mod-${idx}`} direction="row" spacing={1} sx={{ alignItems: "center" }}>
                  <Typography
                    variant="caption"
                    sx={{
                      textDecoration: 'line-through',
                      color: 'text.disabled',
                    }}
                  >
                    {reminder.oldTiming}
                  </Typography>
                  <Typography variant="caption">→</Typography>
                  <Typography variant="caption" sx={{ color: 'primary.main' }}>
                    {reminder.newTiming}
                  </Typography>
                </Stack>
              ))}
            </Stack>
          </Box>
        </>
      )}

      {/* Forms */}
      {(hasFormChanges || currentForms.length > 0) && (
        <>
          <Divider />
          <Box>
            <Typography variant="caption" color="text.secondary" sx={{ mb: 0.5, display: 'block' }}>
              Forms
            </Typography>
            <Stack spacing={0.5}>
              {/* Show unchanged forms first */}
              {unchangedForms.map((form, idx) => (
                <Typography key={`unchanged-${idx}`} variant="caption">
                  {form.name} · {formatAppointmentDuration(form.offset || '')}
                </Typography>
              ))}

              {/* Then show changes */}
              {changes?.forms?.added.map((form, idx) => (
                <Typography key={`add-${idx}`} variant="caption" sx={{ fontWeight: 700 }}>
                  {form.name} · {form.timing}
                </Typography>
              ))}
              {changes?.forms?.removed.map((form, idx) => (
                <Typography
                  key={`rem-${idx}`}
                  variant="caption"
                  sx={{
                    textDecoration: 'line-through',
                    color: 'text.disabled',
                  }}
                >
                  {form.name} · {form.timing}
                </Typography>
              ))}
              {changes?.forms?.modified.map((form, idx) => (
                <Box key={`mod-${idx}`}>
                  <Typography variant="caption" sx={{ fontWeight: 700 }}>
                    {form.name}
                  </Typography>
                  <Stack direction="row" spacing={1} sx={{alignItems: "center",  ml: 2 }}>
                    <Typography
                      variant="caption"
                      sx={{
                        textDecoration: 'line-through',
                        color: 'text.disabled',
                      }}
                    >
                      {form.oldTiming}
                    </Typography>
                    <Typography variant="caption">→</Typography>
                    <Typography variant="caption" sx={{ color: 'primary.main' }}>
                      {form.newTiming}
                    </Typography>
                  </Stack>
                </Box>
              ))}
            </Stack>
          </Box>
        </>
      )}
    </Stack>
  );
};
